import React, { useState, useEffect } from 'react';
import { Calendar, MapPin, CheckCircle, Package, DollarSign, ChevronDown, ChevronUp, Download, Share2, FileText, Map as MapIcon } from 'lucide-react';
import RouteMapView from './RouteMapView';
import api from '../services/api';
import toast from 'react-hot-toast';

const MessengerHistoryView = () => {
    const [routes, setRoutes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedRoute, setExpandedRoute] = useState(null);
    const [filter, setFilter] = useState('week'); // today, yesterday, week, month

    const loadRouteHistory = async () => {
        try {
            setLoading(true);

            // Calculate date range based on filter
            const now = new Date();
            let startDate;

            switch (filter) {
                case 'today':
                    startDate = new Date(now.setHours(0, 0, 0, 0));
                    break;
                case 'yesterday':
                    startDate = new Date(now.setDate(now.getDate() - 1));
                    startDate.setHours(0, 0, 0, 0);
                    break;
                case 'week':
                    startDate = new Date(now.setDate(now.getDate() - 7));
                    break;
                case 'month':
                    startDate = new Date(now.setMonth(now.getMonth() - 1));
                    break;
                default:
                    startDate = new Date(now.setDate(now.getDate() - 7));
            }

            const response = await api.get(`/routes/messenger/history?startDate=${startDate.toISOString()}`);
            setRoutes(response.data.routes || []);
        } catch (error) {
            console.error('Error loading route history:', error);
            toast.error('Error al cargar historial de rutas');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadRouteHistory();
    }, [filter]);

    const toggleRouteExpansion = (routeId) => {
        setExpandedRoute(expandedRoute === routeId ? null : routeId);
    };

    const handleDownloadPDF = async (routeId) => {
        try {
            toast.loading('Generando PDF...', { id: 'pdf-loading' });
            const response = await api.get(`/routes/${routeId}/report-pdf`, {
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Reporte_Ruta_${routeId}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success('Reporte descargado', { id: 'pdf-loading' });
        } catch (error) {
            console.error('Error downloading PDF:', error);
            toast.error('Error al generar el PDF', { id: 'pdf-loading' });
        }
    };

    const handleShareWhatsApp = (routeId, routeNumber) => {
        const baseUrl = window.location.origin;
        const pdfUrl = `${api.defaults.baseURL}/routes/${routeId}/report-pdf`;
        const message = `👋 Hola Jefe de Logística. Adjunto el reporte de la Ruta #${routeNumber || routeId}.\n\nPuedes descargarlo aquí: ${pdfUrl}`;
        const encodedMessage = encodeURIComponent(message);
        window.open(`https://wa.me/?text=${encodedMessage}`, '_blank');
    };

    const filters = [
        { id: 'today', label: 'Hoy' },
        { id: 'yesterday', label: 'Ayer' },
        { id: 'week', label: 'Semana' },
        { id: 'month', label: 'Mes' }
    ];

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Filter Buttons */}
            <div className="flex gap-2 overflow-x-auto pb-2">
                {filters.map(f => (
                    <button
                        key={f.id}
                        onClick={() => setFilter(f.id)}
                        className={`px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-colors ${filter === f.id
                            ? 'bg-blue-600 text-white shadow-sm'
                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                            }`}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            {/* Routes List */}
            {routes.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                    <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">No hay rutas en este período</h3>
                    <p className="text-gray-500">Intenta seleccionar un rango de fechas diferente</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {routes.map(route => {
                        const isExpanded = expandedRoute === route.id;
                        const totalOrders = route.orders?.length || 0;
                        const deliveredOrders = route.orders?.filter(o => o.delivery_status === 'entregado').length || 0;
                        const totalCash = route.orders?.reduce((sum, o) => sum + (parseFloat(o.payment_collected) || 0), 0) || 0;

                        return (
                            <div key={route.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                                {/* Route Header */}
                                <div
                                    className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                                    onClick={() => toggleRouteExpansion(route.id)}
                                >
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                                                <CheckCircle className="w-6 h-6 text-green-600" />
                                            </div>
                                            <div>
                                                <h3 className="font-bold text-lg text-gray-900">Ruta #{route.route_number || route.id}</h3>
                                                <p className="text-sm text-gray-500">
                                                    {new Date(route.route_date).toLocaleDateString('es-CO', {
                                                        weekday: 'long',
                                                        day: 'numeric',
                                                        month: 'long',
                                                        year: 'numeric'
                                                    })}
                                                </p>
                                            </div>
                                        </div>

                                        <button className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                                            {isExpanded ? (
                                                <ChevronUp className="w-5 h-5 text-gray-600" />
                                            ) : (
                                                <ChevronDown className="w-5 h-5 text-gray-600" />
                                            )}
                                        </button>
                                    </div>

                                    {/* Stats */}
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                        <div className="flex items-center gap-2 text-sm">
                                            <Package className="w-4 h-4 text-blue-600" />
                                            <span className="font-semibold text-gray-900">
                                                {deliveredOrders}/{totalOrders} entregados
                                            </span>
                                        </div>

                                        <div className="flex items-center gap-2 text-sm">
                                            <DollarSign className="w-4 h-4 text-green-600" />
                                            <span className="font-semibold text-gray-900">
                                                ${totalCash.toLocaleString('es-CO')}
                                            </span>
                                        </div>

                                        <div className="flex items-center gap-2 text-sm">
                                            <MapPin className="w-4 h-4 text-gray-600" />
                                            <span className="font-medium text-gray-700">
                                                {totalOrders} paradas
                                            </span>
                                        </div>

                                        <div className="flex gap-2">
                                            <button
                                                onClick={(e) => { e.stopPropagation(); handleDownloadPDF(route.id); }}
                                                className="flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded border border-blue-200 hover:bg-blue-100 transition-colors"
                                                title="Descargar PDF"
                                            >
                                                <Download className="w-3 h-3" />
                                                Reporte
                                            </button>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); handleShareWhatsApp(route.id, route.route_number); }}
                                                className="flex items-center gap-1 text-xs bg-green-50 text-green-700 px-2 py-1 rounded border border-green-200 hover:bg-green-100 transition-colors"
                                                title="Compartir WhatsApp"
                                            >
                                                <Share2 className="w-3 h-3" />
                                                Enviar
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Expanded Details */}
                                {isExpanded && route.orders && (
                                    <div className="border-t border-gray-200 bg-gray-50 p-4">
                                        <h4 className="font-semibold text-gray-900 mb-3">Pedidos de la ruta</h4>
                                        <div className="space-y-2">
                                            {route.orders.map((order, idx) => (
                                                <div
                                                    key={order.id}
                                                    className="bg-white p-3 rounded-lg border border-gray-200 flex items-center justify-between"
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <span className="w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-bold">
                                                            {idx + 1}
                                                        </span>
                                                        <div>
                                                            <p className="font-medium text-gray-900">#{order.order_number}</p>
                                                            <p className="text-xs text-gray-500">{order.customer_name}</p>
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center gap-2">
                                                        {order.delivery_status === 'entregado' ? (
                                                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                                                ✓ Entregado
                                                            </span>
                                                        ) : (
                                                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-600">
                                                                Pendiente
                                                            </span>
                                                        )}

                                                        {order.payment_collected > 0 && (
                                                            <span className="text-sm font-semibold text-green-700">
                                                                ${parseFloat(order.payment_collected).toLocaleString('es-CO')}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="mt-6">
                                            <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                                                <MapIcon className="w-5 h-5 text-blue-600" />
                                                Mapa del Recorrido
                                            </h4>
                                            <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm transition-all hover:shadow-md">
                                                <RouteMapView
                                                    route={{
                                                        orders: route.orders.filter(o => o.type === 'order').map(o => ({
                                                            ...o,
                                                            orderNumber: o.order_number,
                                                            customerName: o.customer_name,
                                                            deliveryAddress: o.customer_address,
                                                            deliveryStatus: o.delivery_status,
                                                            sequenceNumber: o.sequence_number
                                                        })),
                                                        additionalStops: route.orders.filter(o => o.type === 'stop').map(s => ({
                                                            ...s,
                                                            sequenceNumber: s.sequence_number
                                                        }))
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default MessengerHistoryView;
